import { useQueryClient } from '@tanstack/react-query';

import type {
  BookingFeedbackForm,
  BookingFeedbackFormKind,
  BookingFeedbackSpace,
} from '@mds/shared/types/booking-feedback';

import * as feedback from '@/bookings/booking-feedback.api';
import FormBuilder, { LockedRow } from '@/components/events/form-builder';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import {
  BOOKING_FEEDBACK_FORM_CHANGED,
  FORM_KIND_LABELS,
  bookingFeedbackDraftOf,
  isNoOwnForm,
  ownFormFrom,
  replaceRequestOf,
} from '@/domain/booking-feedback';
import { stamp } from '@mds/shared/lib/dates';

/** A kind's default form, or one space's own. */
export type FeedbackFormTarget =
  { kind: BookingFeedbackFormKind } | { space: BookingFeedbackSpace };

const DEFAULT_COVERS: Record<BookingFeedbackFormKind, string> = {
  desk: 'both coworking floors',
  space: 'every room and facility',
};

/** A space's own form, or a copy of its default to start one from. */
async function loadSpaceForm(space: BookingFeedbackSpace): Promise<BookingFeedbackForm> {
  try {
    return await feedback.getSpaceForm(space.id);
  } catch (error) {
    if (!isNoOwnForm(error)) throw error;
    return ownFormFrom(await feedback.getDefaultForm(space.kind), space.id);
  }
}

/**
 * One booking feedback form: a kind's default, or a space's own. Rating and comment are always
 * asked and cannot be changed, so they are drawn as locked rows; everything else is optional
 * sections and questions. A form never freezes: each response keeps a copy of what it answered.
 */
export default function BookingFeedbackBuilder({
  target,
  actionsSlot,
}: {
  target: FeedbackFormTarget;
  /** Where Save and Discard are drawn. */
  actionsSlot: HTMLElement | null;
}) {
  const queryClient = useQueryClient();
  const space = 'space' in target ? target.space : null;
  const kind = space ? space.kind : (target as { kind: BookingFeedbackFormKind }).kind;
  const defaultName = `${FORM_KIND_LABELS[kind]} default`;

  return (
    <FormBuilder
      queryKey={
        space
          ? ['booking-feedback-form', 'space', space.id]
          : ['booking-feedback-form', 'default', kind]
      }
      load={() => (space ? loadSpaceForm(space) : feedback.getDefaultForm(kind))}
      save={(draft, version) =>
        space
          ? feedback.replaceSpaceForm(space.id, replaceRequestOf(draft, version))
          : feedback.replaceDefaultForm(kind, replaceRequestOf(draft, version))
      }
      actionsSlot={actionsSlot}
      draftOf={bookingFeedbackDraftOf}
      changedCode={BOOKING_FEEDBACK_FORM_CHANGED}
      managePermission="reservations.manage"
      noun="feedback form"
      savedNotice={() =>
        space
          ? `Saved. ${space.name} asks this form from now on.`
          : `Saved. Every space without a form of its own asks this one from now on.`
      }
      onSaved={() => {
        // The spaces list says which spaces have a form of their own.
        void queryClient.invalidateQueries({ queryKey: ['booking-feedback-spaces'] });
      }}
      questionsHint="Optional. Questions here come straight after the rating. Group related ones under a section heading, and clients see them together."
      intro={({ loaded, mayManage }) => {
        const unsaved = space !== null && loaded.version === 0;
        return (
          <Card
            title={space ? `${space.name} form` : `${defaultName} form`}
            action={
              <div className="flex flex-wrap items-center gap-2">
                {unsaved ? (
                  <Chip className="border border-warn-edge bg-warn-wash text-warn-text">
                    Not saved · copied from the {defaultName.toLowerCase()}
                  </Chip>
                ) : (
                  <Chip className="border border-brand-edge/35 bg-brand-wash text-brand-strong">
                    {space ? 'Own form' : 'Default'}
                  </Chip>
                )}
                {loaded.updatedAt && <Chip>Saved {stamp(loaded.updatedAt)}</Chip>}
              </div>
            }
          >
            <div className="flex flex-col gap-4 text-base">
              <p className="m-0 text-ink-secondary">
                {space
                  ? unsaved
                    ? `This starts as a copy of the ${defaultName.toLowerCase()} form. Change it and save, and ${space.name} asks this form instead. The default stays as it is for every other space.`
                    : `${space.name} asks this form instead of the ${defaultName.toLowerCase()} form.`
                  : `Asked after bookings of ${DEFAULT_COVERS[kind]}, unless a space has a form of its own.`}
              </p>
              <p className="m-0 text-ink-muted">
                Each client is asked once per space, the first time they book it, while that space
                is switched on. Editing a form changes it for clients asked from now on; answers
                already given keep their questions.
              </p>
              {!mayManage && (
                <p className="m-0 text-ink-muted">You can read this form but not change it.</p>
              )}
            </div>
          </Card>
        );
      }}
      alwaysAsked={(loaded) => (
        <>
          <LockedRow
            label="Overall, how would you rate it?"
            detail={`Rating ${loaded.standardFields.overallRating.min}–${loaded.standardFields.overallRating.max} · required`}
          />
          <LockedRow
            label="Anything else you would like to tell us?"
            detail={`Comment · optional · up to ${loaded.standardFields.comment.maxLength.toLocaleString()} characters`}
          />
        </>
      )}
    />
  );
}
